import React, { useMemo } from 'react';
import { Marker, Tooltip, Popup } from 'react-leaflet';
import L from 'leaflet';
import {
  NormalizedGaushala,
  DATA_SOURCE_COLORS,
  CONFIDENCE_LEVELS,
} from '../../types/gaushalaMap.types';
import MarkerPopup from './MarkerPopup';
import DataSourceIndicator from './DataSourceIndicator';

/**
 * Gaushala Marker Component
 *
 * Displays a marker for each gaushala on the map with:
 * - Custom icon based on data source
 * - Tooltip showing gaushala name
 * - Clickable popup with details
 * - Visual indicator for data source
 */

interface GaushalaMarkerProps {
  gaushala: NormalizedGaushala;
  onClick?: (gaushala: NormalizedGaushala) => void;
  isSelected?: boolean;
}

/**
 * Create custom Leaflet icon
 */
const createMarkerIcon = (dataSource: string, isSelected: boolean = false) => {
  const baseColor =
    DATA_SOURCE_COLORS[dataSource as keyof typeof DATA_SOURCE_COLORS] ||
    DATA_SOURCE_COLORS.fallback;
  const size = isSelected ? 40 : 32;
  const shadowSize = isSelected ? 45 : 40;

  const html = `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${size}px;
      height: ${size}px;
      background-color: ${baseColor};
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3)${isSelected ? ', 0 0 12px ' + baseColor : ''};
      color: white;
      font-size: 18px;
      font-weight: bold;
      cursor: pointer;
      transition: transform 0.2s;
    ">
      🐄
    </div>
  `;

  return L.divIcon({
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
    className: 'gaushala-marker',
  });
};

const GaushalaMarker: React.FC<GaushalaMarkerProps> = ({
  gaushala,
  onClick,
  isSelected = false,
}) => {
  const markerIcon = useMemo(
    () => createMarkerIcon(gaushala.dataSource, isSelected),
    [gaushala.dataSource, isSelected]
  );

  const tooltipContent = useMemo(
    () => (
      <div className="text-sm">
        <p className="font-semibold">{gaushala.name}</p>
        <p className="text-xs text-gray-600">{gaushala.address}</p>
      </div>
    ),
    [gaushala.name, gaushala.address]
  );

  const handleMarkerClick = () => {
    if (onClick) {
      onClick(gaushala);
    }
  };

  return (
    <Marker
      position={[gaushala.latitude, gaushala.longitude]}
      icon={markerIcon}
      eventHandlers={{
        click: handleMarkerClick,
      }}
    >
      {/* Hover Tooltip */}
      <Tooltip
        permanent={false}
        direction="top"
        offset={[0, -10]}
        className="gaushala-tooltip"
      >
        <div className="tooltip-content">
          <p className="font-semibold text-gray-800">{gaushala.name}</p>
          <p className="text-xs text-gray-600 truncate max-w-xs">
            {gaushala.address}
          </p>
          <DataSourceIndicator
            dataSource={gaushala.dataSource}
            confidence={gaushala.confidence}
            showLabel={true}
          />
        </div>
      </Tooltip>

      {/* Click Popup */}
      <Popup maxWidth={350} minWidth={250} className="gaushala-popup">
        <MarkerPopup
          gaushala={gaushala}
          onClose={() => {
            // Close popup by clicking outside or escape
          }}
        />
      </Popup>
    </Marker>
  );
};

export default GaushalaMarker;
